/**
 * Shared match-quality bands (hiring analysis).
 * Thresholds must stay in sync with api/apps/evaluations/bands.py.
 *
 * strong ≥ 0.80 · good ≥ 0.60 · mixed ≥ 0.40 · limited ≥ 0.20 · else poor
 */

export type MatchBand = 'strong' | 'good' | 'mixed' | 'limited' | 'poor'

/** Legacy API/saved-job value — treat as mixed (“Worth a look”). */
export type LegacyMatchBand = 'possible' | 'moderate' | 'weak'

export type MatchBandInput = MatchBand | LegacyMatchBand | null | undefined

export const MATCH_BANDS: readonly MatchBand[] = [
  'strong',
  'good',
  'mixed',
  'limited',
  'poor',
] as const

/** Normalize legacy band strings from older payloads. */
export function normalizeMatchBand(band: MatchBandInput): MatchBand | null {
  if (band == null) return null
  if (band === 'possible' || band === 'moderate') return 'mixed'
  if (band === 'weak') return 'limited'
  if ((MATCH_BANDS as readonly string[]).includes(band)) return band as MatchBand
  return null
}

/** Canonical score → band. score is 0–1. */
export function matchBand(score: number): MatchBand {
  if (score >= 0.8) return 'strong'
  if (score >= 0.6) return 'good'
  if (score >= 0.4) return 'mixed'
  if (score >= 0.2) return 'limited'
  return 'poor'
}

/** Ring / numeral text class keyed to band. */
export function matchBandTextClassForBand(band: MatchBand): string {
  if (band === 'strong') return 'text-success'
  if (band === 'good') return 'text-primary'
  if (band === 'mixed') return 'text-muted-foreground'
  if (band === 'limited') return 'text-warning'
  return 'text-destructive'
}

/** Bar fill class keyed to band. */
export function matchBandBarClassForBand(band: MatchBand): string {
  if (band === 'strong') return 'bg-success/70'
  if (band === 'good') return 'bg-primary/70'
  if (band === 'mixed') return 'bg-muted-foreground/50'
  if (band === 'limited') return 'bg-warning/70'
  return 'bg-destructive/70'
}

/** Bar fill class keyed to score. Shared default for EvaluationDimensionRow. */
export function matchBandBarClass(score: number): string {
  return matchBandBarClassForBand(matchBand(score))
}

/** Numeral text class keyed to score. Pairs with matchBandBarClass. */
export function matchBandTextClass(score: number): string {
  return matchBandTextClassForBand(matchBand(score))
}

/** Sort rank: strong first (0) … poor last (4). Unknown/null last. */
export function matchBandRank(band: MatchBandInput): number {
  const normalized = normalizeMatchBand(band)
  if (!normalized) return 5
  return MATCH_BANDS.indexOf(normalized)
}
